import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  MapPin,
  UserCheck,
  UserX,
  Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const EventAttendancePage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();
  
  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingUser, setProcessingUser] = useState(null);

  useEffect(() => {
    fetchEventDetails();
    fetchAttendees();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      const response = await api.get(`/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'événement');
    }
  };

  const fetchAttendees = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/events/${eventId}/attendees`);
      setAttendees(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des participants');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (userId) => {
    setProcessingUser(userId);
    try {
      await api.post(`/events/${eventId}/validate-attendance/${userId}`);
      toast.success('Présence validée ! L\'utilisateur a reçu ses XP.');
      fetchAttendees();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setProcessingUser(null);
    }
  };

  const handleInvalidate = async (userId) => {
    setProcessingUser(userId);
    try {
      await api.post(`/events/${eventId}/invalidate-attendance/${userId}`);
      toast.success('Présence invalidée. XP retirés.');
      fetchAttendees();
    } catch (error) {
      toast.error('Erreur lors de l\'invalidation');
    } finally {
      setProcessingUser(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'checked_in':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Présent (Check-in)</Badge>;
      case 'validated':
        return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30"><UserCheck className="w-3 h-3 mr-1" />Validé par admin</Badge>;
      case 'registered':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Inscrit (RSVP)</Badge>;
      case 'invalidated':
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Invalidé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'major')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Accès non autorisé</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold">Gestion des présences</h1>
              <p className="text-sm text-muted-foreground">Valider ou invalider les participations</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Event Info */}
        {event && (
          <Card className="mb-6 border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">{event.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {format(parseISO(event.start_time), 'EEEE d MMMM yyyy', { locale: fr })}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {format(parseISO(event.start_time), 'HH:mm')} - {format(parseISO(event.end_time), 'HH:mm')}
                </div>
                {event.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-md bg-secondary/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{attendees.length}</p>
              <p className="text-xs text-muted-foreground">Total inscrits</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-green-500/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-500">
                {attendees.filter(a => a.status === 'checked_in' || a.status === 'validated').length}
              </p>
              <p className="text-xs text-muted-foreground">Présents confirmés</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-yellow-500/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-500">
                {attendees.filter(a => a.status === 'registered').length}
              </p>
              <p className="text-xs text-muted-foreground">En attente</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-red-500/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-500">
                {attendees.filter(a => a.status === 'invalidated').length}
              </p>
              <p className="text-xs text-muted-foreground">Invalidés</p>
            </CardContent>
          </Card>
        </div>

        {/* Attendees List */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Liste des participants ({attendees.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : attendees.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun participant inscrit</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {attendees.map((attendee) => (
                    <div 
                      key={attendee.user_id}
                      className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={attendee.avatar_url} />
                          <AvatarFallback className="bg-primary/20">
                            {attendee.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{attendee.name}</p>
                          <p className="text-sm text-muted-foreground">{attendee.email}</p>
                          {attendee.checked_in_at && (
                            <p className="text-xs text-muted-foreground">
                              Check-in: {format(parseISO(attendee.checked_in_at), 'HH:mm', { locale: fr })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(attendee.status)}
                        
                        {/* Action buttons */}
                        {attendee.status === 'registered' && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600 text-white"
                              onClick={() => handleValidate(attendee.user_id)}
                              disabled={processingUser === attendee.user_id}
                            >
                              {processingUser === attendee.user_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4 mr-1" />
                                  Valider
                                </>
                              )}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                              onClick={() => handleInvalidate(attendee.user_id)}
                              disabled={processingUser === attendee.user_id}
                            >
                              <UserX className="w-4 h-4 mr-1" />
                              Rejeter
                            </Button>
                          </>
                        )}
                        
                        {(attendee.status === 'checked_in' || attendee.status === 'validated') && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                            onClick={() => handleInvalidate(attendee.user_id)}
                            disabled={processingUser === attendee.user_id}
                          >
                            {processingUser === attendee.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <UserX className="w-4 h-4 mr-1" />
                                Invalider
                              </>
                            )}
                          </Button>
                        )}
                        
                        {attendee.status === 'invalidated' && (
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600 text-white"
                            onClick={() => handleValidate(attendee.user_id)}
                            disabled={processingUser === attendee.user_id}
                          >
                            {processingUser === attendee.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4 mr-1" />
                                Réactiver
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EventAttendancePage;
